import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { deleteCookie, useEffectOnce } from '@chilecompra/react-kit'
import { useAnalyticsPageView, useAnalyticsTag } from '@chilecompra/react-kit/analytics'
import { Button, Toast, Typography, useViewport } from '@chilecompra/react-kit/components'
import { closeModal, onRegisterBy, openModal } from '../RegisterWizard.actions'
import MustBeRegisterModal from './RegisterWizardWelcome.mustBeRegisterModal'
import VolunteersModal from './RegisterWizardWelcome.volunteersInstitutionsModal'
import {
  Body,
  ButtonBox,
  Column,
  Link,
  List,
  ListItem,
  Root,
  Section,
  Strong,
  Title,
  WelcomeImage
} from './RegisterWizardWelcome.styles'
import { REACT_APP_HELP_FIRST_STEPS_URL } from '../../../config/settings/environment'

/**
 * The RegisterWizardWelcome's container.
 */
const RegisterWizardWelcome = props => {
  const { onChangeStep } = props
  const { size } = useViewport()
  const dispatch = useDispatch()
  const {
    modals: { mustBeRegister, volunteersInstitutions }
  } = useSelector(state => state.registerWizard)

  const handleRegisterByUniquePass = () => {
    dispatch(onRegisterBy('by-unique-pass'))
    onChangeStep('by-unique-pass')
  }

  useEffectOnce(() => {
    localStorage.removeItem('registerWizardBackup')
    deleteCookie('dniTestCookie')
    deleteCookie('signWithCU')
  })

  const handleOpenMustBeRegisterModal = event => {
    if (event.type === 'click' || (event.type === 'keypress' && [13, 32].includes(event.charCode)))
      dispatch(openModal('mustBeRegister'))
  }

  const handleOpenVolunteersModal = event => {
    if (event.type === 'click' || (event.type === 'keypress' && [13, 32].includes(event.charCode)))
      dispatch(openModal('volunteersInstitutions'))
  }

  const handleCloseModal = useCallback(
    id => {
      return () => {
        dispatch(closeModal(id))
      }
    },
    [dispatch]
  )

  const renderFaqSection = () => {
    return (
      <Section>
        <Typography fontWeight="bold" variant="body2">
          Preguntas frecuentes:
        </Typography>
        <List>
          <ListItem>
            <Link
              color="black2"
              tabIndex="0"
              aria-label="¿Mi institución debe registrarse en Mercado Público?"
              onClick={handleOpenMustBeRegisterModal}
              onKeyPress={handleOpenMustBeRegisterModal}
            >
              ¿Mi institución debe registrarse en Mercado Público?
            </Link>
          </ListItem>
          <ListItem>
            <Link
              color="black2"
              tabIndex="0"
              aria-label="¿Puedo registrar mi institución de forma voluntaria?"
              onClick={handleOpenVolunteersModal}
              onKeyPress={handleOpenVolunteersModal}
            >
              ¿Puedo registrar mi institución de forma voluntaria?
            </Link>
          </ListItem>
          <ListItem>
            <Link
              color="black2"
              tabIndex="0"
              aria-label="¿Tu organismo se suma al sistema de compras públicas?"
              rel="noopener noreferrer"
              target="_blank"
              href={REACT_APP_HELP_FIRST_STEPS_URL}
            >
              ¿Tu organismo se suma al sistema de compras públicas?
            </Link>
          </ListItem>
        </List>
      </Section>
    )
  }

  const renderDescriptionSection = () => {
    return (
      <Section fontSize="16px" fontWeight="400" lineHeight="20px" marginTop={size.isTiny ? '21px' : '24px'}>
        <Toast tabIndex="0" width={size.isTiny ? '' : '90%'} disableIcon padding="8px 16px" severity="info">
          La persona que realice el proceso de registro <Strong>será asignada como administrador</Strong> de la
          institución en Mercado Público. Para esto es necesario contar con acceso a la Clave Tributaria de la
          institución.
        </Toast>
      </Section>
    )
  }

  useAnalyticsPageView('/landing')
  useAnalyticsTag({ page: 'registro_inicio' })

  return (
    <Root>
      <Body container direction={size.isTiny ? 'column-reverse' : 'row'} spacing={size.isDownToLarge ? 3 : 6}>
        <Column lg={7} md={6} sm={6} xs={12}>
          <Title tabIndex="0">
            Te damos la bienvenida al proceso de inscripción de organismos compradores en Mercado Público
          </Title>
          <Section
            tabIndex="0"
            width={size.isTiny ? '100%' : size.isDownToLarge ? '90%' : '80%'}
            fontSize="16px"
            fontWeight="400"
            lineHeight="20px"
            marginTop={size.isTiny ? '15px' : size.isDownToLarge ? '18px' : '33px'}
          >
            Al registrar a tu institución en la plataforma, podrás realizar y transparentar todas las compras que
            realices utilizando financiamiento público.
          </Section>
          {(size.isUpToLarge || size.isTiny) && renderDescriptionSection()}

          <Section marginTop={size.isTiny ? '21px' : size.isDownToLarge ? '30px' : '50px'}>
            <ButtonBox>
              <Button
                aria-label="Comenzar proceso de inscripción"
                tabIndex="0"
                color="primary"
                onClick={handleRegisterByUniquePass}
              >
                Comenzar proceso de inscripción
              </Button>
            </ButtonBox>
          </Section>

          {!size.isTiny && size.isDownToLarge && renderDescriptionSection()}
          {!size.isUpToLarge && size.isDownToLarge && renderFaqSection()}
        </Column>
        <Column lg={5} md={6} sm={6} xs={12} className={size.isDownToLarge && 'centerOnTablet'}>
          <WelcomeImage
            tabIndex="0"
            src="/assets/img/steps/index-registro.svg"
            alt="Registrarse como comprador en Mercado Público"
          />
          {size.isUpToLarge && renderFaqSection()}
        </Column>
      </Body>
      <MustBeRegisterModal open={mustBeRegister?.open} onClose={handleCloseModal('mustBeRegister')} />
      <VolunteersModal open={volunteersInstitutions?.open} onClose={handleCloseModal('volunteersInstitutions')} />
    </Root>
  )
}

RegisterWizardWelcome.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardWelcome.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardWelcome
